import React, { useState, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import Checkbox from '../../../Form/Checkbox';
import AdminService from '../../../../services/AdminService';
import CandidatesService from '../../../../services/CandidatesService';
import classes from './styles.module.scss';
import DotMenu from '../../../ApplicationBadge/DotMenu';
import { CandidatesContext } from '../../../../context/CandidatesContext';
// Feature flag for selection functionality
const ENABLE_SELECTION = false;

export default function CandidateCard({ candidate, isSelected, onToggle }) {
  const { t } = useTranslation();
  const { showIntelCandidateModal } = useContext(CandidatesContext);
  const [showSubmissionsTooltip, setShowSubmissionsTooltip] = useState(false);
  const [localIsFavorite, setLocalIsFavorite] = useState(
    candidate.isFavorite || false
  );
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const [isLoadingCareerInfo, setIsLoadingCareerInfo] = useState(false);

  const {
    id,
    firstName,
    lastName,
    avatar,
    jobTitle,
    company,
    phone,
    source,
    submissionsCount,
    submissionsJobApplications,
    lastViewed,
    lastActivity,
    matchSections,
    metrics,
    matchPercentage,
  } = candidate;

  const getInitials = () => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
  };

  const formatRelativeTime = (date) => {
    if (!date) return null;
    return moment(date).fromNow();
  };

  const toggleFavorite = async (event) => {
    event.stopPropagation();
    if (isTogglingFavorite) return;

    const newFavoriteState = !localIsFavorite;
    setLocalIsFavorite(newFavoriteState);
    setIsTogglingFavorite(true);

    try {
      if (newFavoriteState) {
        await AdminService.addCandidateToFavorites({ id });
      } else {
        await AdminService.removeCandidateFromFavorites({ id });
      }
    } catch (error) {
      // Revert on error
      setLocalIsFavorite(!newFavoriteState);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const onEditCandidate = async () => {
    if (isLoadingCareerInfo || !id) return;

    setIsLoadingCareerInfo(true);
    try {
      const careerInfo = await CandidatesService.getCandidateInfo(id);
      showIntelCandidateModal(careerInfo);
    } catch (error) {
      // Failed to fetch candidate career info
      // Optionally show an error message to the user
    } finally {
      setIsLoadingCareerInfo(false);
    }
  };

  const handleCardClick = () => {
    onToggle();
  };

  return (
    <div
      className={classNames(classes.CandidateCard, {
        [classes.selected]: isSelected,
      })}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <div className={classes.header}>
        <div className={classes.leftHeader}>
          <div className={classes.checkboxAvatarGroup}>
            {ENABLE_SELECTION && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onToggle();
                }}
              >
                <Checkbox isSelected={isSelected} onChange={() => {}} />
              </div>
            )}

            <div className={classes.avatarContainer}>
              {avatar ? (
                <img src={avatar} alt={`${firstName} ${lastName}`} />
              ) : (
                <div className={classes.avatar}>{getInitials()}</div>
              )}
            </div>
          </div>

          <div className={classes.candidateInfo}>
            <div className={classes.nameRow}>
              <h3 className={classes.name}>
                {firstName} {lastName}
              </h3>
              <div
                className={classNames(classes.sourceTag, {
                  [classes.applied]: source === 'Applied',
                })}
              >
                {source}
              </div>
            </div>
            {(jobTitle !== 'N/A' || company !== 'N/A') && (
              <div className={classes.jobInfo}>
                {jobTitle !== 'N/A' && (
                  <span className={classes.jobTitle}>{jobTitle}</span>
                )}
                {company !== 'N/A' && (
                  <span className={classes.company}>({company})</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={classes.rightHeader}>
          {submissionsCount > 0 && (
            <div
              className={classes.submissionsCount}
              onMouseEnter={() => setShowSubmissionsTooltip(true)}
              onMouseLeave={() => setShowSubmissionsTooltip(false)}
            >
              {submissionsCount}{' '}
              {submissionsCount === 1 ? 'SUBMISSION' : 'SUBMISSIONS'}
              {showSubmissionsTooltip &&
                submissionsJobApplications.length > 0 && (
                  <div className={classes.submissionsTooltip}>
                    <div className={classes.tooltipHeader}>
                      {t('intelSearch.candidateCard.submissions')}
                    </div>
                    <div className={classes.tooltipContent}>
                      {submissionsJobApplications.map((submission) => (
                        <div
                          key={submission.jobId}
                          className={classes.submissionItem}
                        >
                          <div className={classes.submissionJobId}>
                            #{submission.jobId}
                          </div>
                          <div className={classes.submissionDetails}>
                            <div className={classes.submissionJobTitle}>
                              {submission.jobTitle}
                            </div>
                            <div className={classes.submissionCompany}>
                              {submission.companyName}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
          <button
            type="button"
            className={classNames(classes.favoriteButton, {
              [classes.isFavorite]: localIsFavorite,
            })}
            onClick={toggleFavorite}
            disabled={isTogglingFavorite}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <defs>
                <linearGradient
                  x1="10"
                  y1="-11.2662"
                  x2="-12.5324"
                  y2="8.73379"
                  gradientUnits="userSpaceOnUse"
                  id="favoriteGradient"
                >
                  <stop stopColor="#00E4AC" />
                  <stop offset="0.504968" stopColor="#10C5E9" />
                  <stop offset="1" stopColor="#9240DC" />
                </linearGradient>
                <linearGradient
                  x1="10"
                  y1="-11.2662"
                  x2="-12.5324"
                  y2="8.73379"
                  gradientUnits="userSpaceOnUse"
                  id="favoriteGradient1"
                >
                  <stop stopColor="#C1C5D6" />
                </linearGradient>
              </defs>
              <circle
                cx="10"
                cy="10"
                r="10"
                className={classes.favoriteCircle}
              />
              <path
                d="M10.5772 7.14936L11.1638 8.33226C11.2438 8.49693 11.4571 8.65487 11.6371 8.68511L12.7003 8.86322C13.3802 8.97748 13.5402 9.47483 13.0502 9.96547L12.2237 10.7989C12.0837 10.94 12.007 11.2122 12.0504 11.4071L12.287 12.4388C12.4736 13.2554 12.0437 13.5713 11.3271 13.1445L10.3305 12.5497C10.1506 12.4422 9.85393 12.4422 9.67061 12.5497L8.67406 13.1445C7.9608 13.5713 7.52752 13.252 7.71416 12.4388L7.9508 11.4071C7.99413 11.2122 7.91747 10.94 7.77749 10.7989L6.95091 9.96547C6.4643 9.47483 6.62095 8.97748 7.30088 8.86322L8.36409 8.68511C8.54074 8.65487 8.75405 8.49693 8.83404 8.33226L9.42064 7.14936C9.74061 6.5075 10.2605 6.5075 10.5772 7.14936Z"
                fill="white"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <DotMenu
            variant="small"
            candidate={candidate}
            editCandidate={onEditCandidate}
          />
        </div>
      </div>

      <div className={classes.matchSections}>
        <div className={classes.matchTag}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.125 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V15C3 15.3978 3.15804 15.7794 3.43934 16.0607C3.72064 16.342 4.10218 16.5 4.5 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V6.375L10.125 1.5Z"
              stroke="#C1C5D6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.125 1.5V6.375H15"
              stroke="#C1C5D6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{t('intelSearch.candidateCard.resume')}</span>
          <div className={classes.dot} />
          <span>{matchSections.resume}</span>
        </div>
        <div className={classes.matchTag}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.125 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V15C3 15.3978 3.15804 15.7794 3.43934 16.0607C3.72064 16.342 4.10218 16.5 4.5 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V6.375L10.125 1.5Z"
              stroke="#C1C5D6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.125 1.5V6.375H15"
              stroke="#C1C5D6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11.25 9.75H6.75"
              stroke="#C1C5D6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11.25 12.75H6.75"
              stroke="#C1C5D6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8.25 6.75H7.5H6.75"
              stroke="#C1C5D6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{t('intelSearch.candidateCard.note')}</span>
          <div className={classes.dot} />
          <span>{matchSections.note}</span>
        </div>
        <div className={classes.matchTag}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="9" cy="9" r="7.5" stroke="#C1C5D6" strokeWidth="1.5" />
            <circle
              cx="9"
              cy="7.5"
              r="2.25"
              stroke="#C1C5D6"
              strokeWidth="1.5"
            />
            <path
              d="M4.5 13.5C4.5 11.8431 5.84315 10.5 7.5 10.5H10.5C12.1569 10.5 13.5 11.8431 13.5 13.5"
              stroke="#C1C5D6"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span>{t('intelSearch.candidateCard.personal')}</span>
          <div className={classes.dot} />
          <span>{matchSections.personal}</span>
        </div>
      </div>

      <div className={classes.footer}>
        <div className={classes.contactInfo}>
          <div className={classes.infoRow}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 3C10.5304 3.16789 11.0118 3.46297 11.4011 3.85982C11.7903 4.25668 12.0754 4.74253 12.2333 5.27778M10 1C11.073 1.19478 12.0604 1.70904 12.8284 2.47703C13.5964 3.24501 14.1106 4.23249 14.3053 5.30556M13.5 11.2V12.7C13.5008 12.8321 13.4755 12.9632 13.4258 13.0854C13.376 13.2076 13.3027 13.3184 13.2104 13.4116C13.118 13.5049 13.0083 13.579 12.8872 13.6298C12.7661 13.6806 12.6359 13.707 12.5053 13.7073C10.9555 13.5538 9.46018 13.0627 8.12667 12.2693C6.87893 11.5401 5.80282 10.5618 4.96667 9.39467C4.18333 8.04738 3.70167 6.53581 3.55333 4.96933C3.55333 4.8397 3.57933 4.71057 3.62933 4.5903C3.67933 4.47003 3.75267 4.36123 3.84467 4.26957C3.93667 4.1779 4.04533 4.10497 4.16467 4.0551C4.284 4.00523 4.41267 3.97937 4.54267 3.97933H6.04267C6.29667 3.97667 6.54333 4.06457 6.74 4.22897C6.93667 4.39337 7.07267 4.62423 7.12267 4.8793C7.21667 5.38897 7.36667 5.88597 7.57 6.36397C7.64267 6.54263 7.66467 6.7373 7.63333 6.92697C7.602 7.11663 7.51867 7.29363 7.39133 7.4373L6.74267 8.0773C7.42267 9.3553 8.44333 10.35 9.74667 11.0193L10.3953 10.3793C10.5427 10.2553 10.7233 10.1746 10.9167 10.1446C11.11 10.1146 11.308 10.1359 11.49 10.2066C11.976 10.4066 12.4807 10.5539 12.998 10.6459C13.258 10.6966 13.4927 10.8326 13.6593 11.0286C13.826 11.2246 13.914 11.4693 13.91 11.7193"
                stroke="black"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{phone}</span>
            {formatRelativeTime(lastViewed) && (
              <>
                <div className={classes.divider} />
                <span className={classes.label}>
                  {t('intelSearch.candidateCard.lastViewed')}:
                </span>
                <span>{formatRelativeTime(lastViewed)}</span>
              </>
            )}
            {formatRelativeTime(lastActivity) && (
              <>
                <div className={classes.divider} />
                <span className={classes.label}>
                  {t('intelSearch.candidateCard.lastActivity')}:
                </span>
                <span>{formatRelativeTime(lastActivity)}</span>
              </>
            )}
          </div>

          <div className={classes.metricsRow}>
            {metrics.map((metric) => (
              <div key={metric} className={classes.metricTag}>
                {metric}
              </div>
            ))}
          </div>
        </div>

        {matchPercentage && (
          <div className={classes.matchPercentage}>{matchPercentage}</div>
        )}
      </div>
    </div>
  );
}
